// Shows and applies upload optimisation (src/lib/uploadCompression.js) at the moment a proof / evidence file is chosen, so
// the original and optimised sizes are visible BEFORE anything is uploaded. Only for proofs and financial evidence; never
// for logos, stamps or QR codes, and never for files already stored.
import { useState, useCallback } from "react";
import { prepareUpload, kb } from "../../lib/uploadCompression";
import { text } from "./tokens";

// Optimise a chosen file; calls onFile(preparedFile). Returns [pick, info, busy].
export function useOptimizedPick(onFile) {
    const [info, setInfo] = useState(null), [busy, setBusy] = useState(false);
    const pick = useCallback(async (f) => {
        setInfo(null);
        if (!f) { onFile(null); return null; }
        setBusy(true);
        let r = null;
        try { r = await prepareUpload(f); } catch { r = null; }
        r = r || { file: f, original: f.size, final: f.size, optimized: false, note: "" };
        setInfo(r); setBusy(false); onFile(r.file);
        return r;
    }, [onFile]);
    return [pick, info, busy];
}

export function OptimizeNote({ info, busy }) {
    if (busy) return <div style={{ ...text.caption, marginTop: 4 }} data-testid="optimize-note">Optimising…</div>;
    if (!info) return null;
    return (
        <div style={{ ...text.caption, marginTop: 4 }} data-testid="optimize-note">
            {info.optimized ? <>Original {kb(info.original)} → optimised <b>{kb(info.final)}</b> (this version is uploaded)</> : <>Size {kb(info.original)}{info.note ? ` · ${info.note}` : ""}</>}
        </div>
    );
}

// A plain file input for evidence (used where a form keeps the file until Save).
export function EvidenceFileInput({ accept, ariaLabel, onFile }) {
    const [pick, info, busy] = useOptimizedPick(onFile);
    return (
        <div>
            <input type="file" accept={accept} aria-label={ariaLabel} onChange={e => pick(e.target.files?.[0] || null)} />
            <OptimizeNote info={info} busy={busy} />
        </div>
    );
}
